import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, RefreshCw, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export const RepairsPanel = ({ repairs, onChange }) => {
  const [busy, setBusy] = useState(null);
  const [showReviewed, setShowReviewed] = useState(false);
  const items = repairs.items.filter((r) => showReviewed || !r.reviewed);
  const run = async (key, call) => {
    setBusy(key);
    try { onChange(await call()); } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };
  const scan = () => run("scan", async () => {
    const { data } = await api.post("/storage/repairs/scan");
    toast.success(data.fixed ? `Repaired ${data.fixed} stranded album${data.fixed === 1 ? "" : "s"}` : "No stranded songs found");
    return { pending: data.pending, items: data.items };
  });
  const review = (id) => run(id, async () => (await api.post(`/storage/repairs/${id}/reviewed`)).data);
  return (
    <div className="mt-6 rounded-md border border-zinc-100 bg-zinc-50/60 p-4" data-testid="storage-repairs">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wrench size={14} className="text-zinc-500" />
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Repaired songs</p>
          {repairs.pending > 0 && <span className="rounded-full bg-amber-100 px-2 py-px font-mono text-[10px] text-amber-800" data-testid="storage-repairs-pending">{repairs.pending} to review</span>}
        </div>
        <div className="flex items-center gap-3">
          {repairs.items.some((r) => r.reviewed) && (
            <button onClick={() => setShowReviewed((v) => !v)} className="text-[11px] text-zinc-500 underline-offset-4 hover:underline" data-testid="storage-repairs-toggle-reviewed">
              {showReviewed ? "Hide reviewed" : "Show reviewed"}
            </button>
          )}
          <button onClick={scan} disabled={busy === "scan"} data-testid="storage-repairs-scan" className="flex h-7 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-3 text-[11px] hover:border-zinc-900 disabled:opacity-50">
            {busy === "scan" ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}Scan now
          </button>
        </div>
      </div>
      <p className="mt-1 text-[11px] text-zinc-500">Songs whose album record went missing after a failed upload are re-attached automatically. Check they look right, then mark them reviewed.</p>
      {items.length === 0 ? (
        <p className="mt-3 text-xs text-zinc-500" data-testid="storage-repairs-empty">{repairs.items.length ? "Everything has been reviewed." : "No stranded songs have needed repair."}</p>
      ) : (
        <ul className="mt-3 divide-y divide-zinc-100" data-testid="storage-repairs-list">
          {items.map((r) => (
            <li key={r.album_id} className="flex items-center gap-3 py-2 text-xs" data-testid={`storage-repair-${r.album_id}`}>
              <div className="min-w-0 flex-1">
                <Link to={`/album/${r.album_id}`} className="truncate font-medium text-zinc-900 hover:underline" data-testid={`storage-repair-link-${r.album_id}`}>{r.name}</Link>
                <p className="truncate text-[11px] text-zinc-500">{r.artist} · {r.songs_now} song{r.songs_now === 1 ? "" : "s"} · <span className="font-mono">{r.account}</span> · repaired {fmtDate(r.repaired_at)}</p>
              </div>
              {r.reviewed ? <span className="flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 size={12} />Reviewed</span> : (
                <button onClick={() => review(r.album_id)} disabled={busy === r.album_id} data-testid={`storage-repair-review-${r.album_id}`} className="flex h-7 items-center gap-1 rounded-full bg-zinc-900 px-3 text-[11px] text-white hover:bg-zinc-700 disabled:opacity-50">
                  {busy === r.album_id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}Mark reviewed
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
